import { Grid, Typography } from '@mui/material';
import { ODSDivider } from '@telekom-ods/react-ui-kit';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router';

import { FormikNumberField } from '../../../../components/Formik';

import type { ComputeOrderValues, UnusedQuota } from '@/types/productTypes';

interface QuotaFieldProps {
  name: string;
  label: string;
  available?: number;
  onWheel?: (event: React.WheelEvent<HTMLInputElement>) => void;
}

const QuotaField = ({
  name,
  label,
  available = 0,
  onWheel = () => {
    /* empty */
  },
}: QuotaFieldProps): React.JSX.Element => {
  const intl = useIntl();

  return (
    <FormikNumberField
      name={name}
      label={label}
      disabled={!available}
      onWheel={onWheel}
      placeholder={intl.formatMessage({ id: 'enterResourceAvailable' }, { label, available })}
    />
  );
};

interface ResourceQuotaProps {
  unusedQuota: UnusedQuota;
  onWheel?: (event: React.WheelEvent<HTMLInputElement>) => void;
}

const ResourceQuota = ({
  unusedQuota,
  onWheel = () => {
    /* empty */
  },
}: ResourceQuotaProps): React.JSX.Element => {
  const intl = useIntl();
  const { setValues } = useFormikContext<ComputeOrderValues>();

  useEffect(() => {
    const unavailableResources: string[] = [];

    if (!unusedQuota.compute.cores)
      unavailableResources.push(intl.formatMessage({ id: 'cpuCores' }));
    if (!unusedQuota.compute.ram) unavailableResources.push(intl.formatMessage({ id: 'ramLabel' }));
    if (!unusedQuota.volume.gigabytes)
      unavailableResources.push(intl.formatMessage({ id: 'diskQuotaLabel' }));
    if (!unusedQuota.network.floatingIps)
      unavailableResources.push(intl.formatMessage({ id: 'floatingIpsLabel' }));

    if (unavailableResources.length > 0) {
      const resourceList = unavailableResources.join(', ');
      const message = intl.formatMessage(
        {
          id:
            unavailableResources.length === 1
              ? 'resourceNotAvailableSingular'
              : 'resourceNotAvailablePlural',
        },
        { resourceList }
      );
      setValues(prev => ({
        ...prev,
        buttonHelperText: message,
      }));
    } else {
      setValues(prev => ({ ...prev, buttonHelperText: '' }));
    }
  }, [
    unusedQuota.compute.cores,
    unusedQuota.compute.ram,
    unusedQuota.volume.gigabytes,
    unusedQuota.network.floatingIps,
    setValues,
    intl,
  ]);

  return (
    <>
      <Grid
        item
        xs={12}
        md={6}
      >
        <Typography>
          <strong>{intl.formatMessage({ id: 'virtualCpusAndMemoryLabel' })}</strong>
          {intl.formatMessage({ id: 'virtualCPUsAndMemory' })}
          <br />
          {intl.formatMessage({ id: 'resourceCalculationInstructions' })}&nbsp;
          <Link to='https://portal.pan-net.cloud/docs/Compute'>
            {intl.formatMessage({ id: 'here' })}
          </Link>
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <Grid
          container
          direction='column'
        >
          <Grid item>
            <QuotaField
              name='cpuCores'
              label={intl.formatMessage({ id: 'cpuCores' })}
              available={unusedQuota.compute.cores}
              onWheel={onWheel}
            />
          </Grid>
          <Grid
            item
            padding='1rem 0'
          >
            <QuotaField
              name='ram'
              label={intl.formatMessage({ id: 'ramGb' })}
              available={unusedQuota.compute.ram}
              onWheel={onWheel}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <ODSDivider variant='horizontal' />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <Typography>
          <strong>{intl.formatMessage({ id: 'storageLabel' })}</strong>{' '}
          {intl.formatMessage({ id: 'storageDescription' })}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
        sx={{ marginTop: '1rem', marginBottom: '1rem' }}
      >
        <QuotaField
          name='diskQuota'
          label={intl.formatMessage({ id: 'diskQuotaGb' })}
          available={unusedQuota.volume.gigabytes}
          onWheel={onWheel}
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        <ODSDivider variant='horizontal' />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <Typography>
          <strong>{intl.formatMessage({ id: 'floatingIpsLabel' })}: </strong>{' '}
          {intl.formatMessage({ id: 'floatingIpsDescription' })}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
        sx={{ marginTop: '1rem', marginBottom: '1rem' }}
      >
        <QuotaField
          name='floatingIps'
          label={intl.formatMessage({ id: 'floatingIpsLabel' })}
          available={unusedQuota.network.floatingIps}
          onWheel={onWheel}
        />
      </Grid>
    </>
  );
};

export default ResourceQuota;
